import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DataTable, ActiveFilters } from "../components/DataTable";
import { useServerList } from "../hooks/useServerList";

const CHANNELS = ["Manual", "Marketplace", "Reseller", "Other"];
const MARKETPLACES = ["Shopee", "Tokopedia", "TikTok Shop", "Lazada", "Blibli", "Website", "WhatsApp", "Instagram"];

export default function Customers() {
  const { can } = useAuth();
  const [groups, setGroups] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const write = can("warehouse_admin", "sales");

  const filters = useMemo(() => ({
    active: activeFilter === "all" ? undefined : activeFilter,
    group_id: groupFilter === "all" ? undefined : groupFilter,
  }), [activeFilter, groupFilter]);

  const L = useServerList("/customers", { initialLimit: 25, filters });
  useEffect(() => { api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)); }, []);
  const groupName = (id) => (groups.find((g) => g.id === id) || {}).name;

  const chips = [];
  if (groupFilter !== "all") chips.push({ key: "group", label: `Group: ${groupName(groupFilter) || groupFilter}`, onClear: () => setGroupFilter("all") });
  if (activeFilter !== "all") chips.push({ key: "active", label: `Status: ${activeFilter === "active" ? "Aktif" : "Nonaktif"}`, onClear: () => setActiveFilter("all") });

  const columns = [
    { key: "number", label: "No.", width: "110px", render: (c) => <span className="font-mono text-xs text-cocoa">{c.number}</span> },
    { key: "name", label: "Nama", sortable: true, render: (c) => <span className="font-semibold text-cocoa block max-w-[220px] truncate" title={c.name}>{c.name}</span> },
    { key: "group_name", label: "Group", render: (c) => <span className="text-sm">{c.group_name || "-"}</span> },
    { key: "phone", label: "Telepon", render: (c) => c.phone || "-" },
    { key: "city", label: "Kota", render: (c) => c.city || "-" },
    { key: "channel", label: "Channel", render: (c) => c.channel || "-" },
    { key: "status", label: "Status", render: (c) => <span className={c.active ? "text-status-passed" : "text-wmuted"}>{c.active ? "Aktif" : "Nonaktif"}</span> },
    { key: "aksi", label: "Aksi", align: "right", render: (c) => write ? <Button size="icon" variant="ghost" onClick={() => { setEdit(c); setOpen(true); }} data-testid={`edit-customer-${c.id}`} title="Edit customer"><Pencil className="w-4 h-4 text-cocoa" /></Button> : null },
  ];

  return (
    <div data-testid="customers-page">
      <PageHeader title="Master Customer" subtitle="Data pelanggan untuk Keep, Sales & Pre-Order">
        {write && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-customer-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Customer</Button>}
      </PageHeader>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={L.q} onChange={(e) => L.setQ(e.target.value)} placeholder="Cari nama / telepon / nomor..." className="pl-9 bg-white" data-testid="customer-search" />
        </div>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-48 bg-white" data-testid="customer-group-filter"><SelectValue placeholder="Group" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Group</SelectItem>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-40 bg-white" data-testid="customer-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <ActiveFilters chips={chips} onReset={() => { setGroupFilter("all"); setActiveFilter("all"); }} />
        <DataTable
          testid="customers-table"
          columns={columns} rows={L.items} loading={L.loading} error={L.error}
          sort={L.sort} onSort={L.toggleSort} rowKey={(c) => c.id}
          page={L.page} pages={L.pages} total={L.total} limit={L.limit}
          onPageChange={L.setPage} onLimitChange={L.setLimit}
          searching={!!L.q} emptyLabel="Belum ada customer."
        />
      </Panel>

      <CustomerDialog open={open} setOpen={setOpen} edit={edit} groups={groups} onDone={L.reload} />
    </div>
  );
}

function CustomerDialog({ open, setOpen, edit, groups, onDone }) {
  const empty = { name: "", phone: "", email: "", address: "", city: "", group_id: "", channel: "Manual", marketplace: "", notes: "", active: true };
  const [form, setForm] = useState(empty);
  const [channels, setChannels] = useState(MARKETPLACES);
  useEffect(() => { if (open) setForm(edit ? { ...empty, ...edit, group_id: edit.group_id || "" } : empty); /* eslint-disable-next-line */ }, [open, edit]);
  useEffect(() => { if (open) api.get("/settings/marketplace-channels").then((r) => setChannels(r.data.channels || MARKETPLACES)).catch(() => {}); }, [open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama customer wajib diisi");
    const payload = {
      name: form.name, phone: form.phone, email: form.email, address: form.address, city: form.city,
      group_id: form.group_id || null, channel: form.channel, marketplace: form.marketplace || "", notes: form.notes, active: form.active,
    };
    try {
      if (edit) await api.put(`/customers/${edit.id}`, payload);
      else await api.post("/customers", payload);
      toast.success("Customer disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Tambah"} Customer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
          <div><Label>Group</Label>
            <Select value={form.group_id || undefined} onValueChange={(v) => setForm({ ...form, group_id: v })}>
              <SelectTrigger data-testid="customer-group-select"><SelectValue placeholder="Pilih group" /></SelectTrigger>
              <SelectContent className="max-h-72">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Channel</Label>
            <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
              <SelectTrigger data-testid="customer-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" /></div>
          <div><Label>Kota</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          {<div className="col-span-2"><Label>Marketplace Channel <span className="text-xs text-wmuted font-normal">(default untuk Shipping Sale)</span></Label>
            <Select value={form.marketplace || undefined} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
              <SelectTrigger data-testid="customer-marketplace-select"><SelectValue placeholder="Pilih marketplace channel" /></SelectTrigger>
              <SelectContent>{channels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="col-span-2"><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="customer-active-switch" /><Label>Aktif</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-customer-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
